import React, { useEffect, useState } from 'react';
import { usePortalShortcutMap } from './portalShortcutContext';
import {
  PORTAL_SHORTCUT_IDS,
  PORTAL_SHORTCUT_LABEL,
  applyPortalShortcutAssignment,
  formatPortalShortcutHint,
  portalChordFromKeyboardEvent,
  portalShortcutCaptureBegin,
  portalShortcutCaptureEnd,
  resetPortalShortcutMap,
  savePortalShortcutMap,
  isPortalShortcutTypingTarget,
  type PortalShortcutId,
} from './portalShortcuts';

/**
 * Belegung einzelner vorhandener Aktionen im Reiter Tasten.
 * Kein Overlay, keine Makros, keine Ketten — eine Kombination pro Funktion, lokal am Sitz.
 */
export const PortalShortcutBelegung: React.FC<{ seatId: string }> = ({ seatId }) => {
  const map = usePortalShortcutMap();
  const [listening, setListening] = useState<PortalShortcutId | null>(null);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!listening) return;
    portalShortcutCaptureBegin();
    const onKey = (event: KeyboardEvent) => {
      if (event.repeat) return;
      if (isPortalShortcutTypingTarget(event.target)) {
        setListening(null);
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const chord = portalChordFromKeyboardEvent(event);
      if (!chord) {
        setNote('Bitte eine Taste drücken, nicht nur Strg, Alt oder Umschalt.');
        return;
      }
      const result = applyPortalShortcutAssignment(map, listening, chord);
      if (result.ok === false) {
        if (result.reason === 'reserved' && result.reserved === 'browser') {
          setNote('Diese Taste behält der Browser. Bitte eine andere wählen.');
        } else if (result.reason === 'taken') {
          setNote(`Diese Taste ist schon für ${PORTAL_SHORTCUT_LABEL[result.takenBy]} belegt.`);
        } else {
          setNote('Diese Taste bleibt wie sie ist. Bitte eine andere wählen.');
        }
        return;
      }
      savePortalShortcutMap(window.localStorage, seatId, result.map);
      setListening(null);
      setNote('');
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      portalShortcutCaptureEnd();
    };
  }, [listening, map, seatId]);

  return (
    <section
      data-testid="portal-shortcut-belegung"
      className="rounded-2xl border border-violet-400/25 bg-[#07070a] p-6 space-y-4"
    >
      <div>
        <h2 className="text-white font-semibold text-base m-0">Zuordnung</h2>
        <p className="text-slate-400 text-sm mt-1 mb-0">
          Taste wählen, dann die gewünschte Kombination drücken. Alt ist keine Pflicht.
        </p>
      </div>
      <ul className="m-0 p-0 list-none space-y-2">
        {PORTAL_SHORTCUT_IDS.map((id) => (
          <li key={id} className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-slate-200 w-52 shrink-0">{PORTAL_SHORTCUT_LABEL[id]}</span>
            <kbd className="shrink-0 rounded border border-violet-400/30 bg-black px-1.5 py-0.5 text-[11px] font-medium text-violet-100">
              {map[id] ? formatPortalShortcutHint(map[id]) : '—'}
            </kbd>
            <button
              type="button"
              data-testid={`portal-shortcut-listen-${id}`}
              onClick={() => {
                setNote('');
                setListening((current) => (current === id ? null : id));
              }}
              className="text-xs font-semibold text-violet-200 hover:text-white"
            >
              {listening === id ? 'Taste drücken…' : 'Taste wählen'}
            </button>
          </li>
        ))}
      </ul>
      {listening ? (
        <button
          type="button"
          onClick={() => {
            setListening(null);
            setNote('');
          }}
          className="text-xs text-slate-400 hover:text-slate-200"
        >
          Auswahl abbrechen
        </button>
      ) : null}
      {note ? <p className="text-amber-300/90 text-xs m-0">{note}</p> : null}
      <p className="text-slate-500 text-xs m-0 leading-relaxed">
        Manche Tasten behält der Browser, zum Beispiel Strg+T oder Alt+D.
      </p>
      <button
        type="button"
        data-testid="portal-shortcut-reset"
        onClick={() => {
          resetPortalShortcutMap(window.localStorage, seatId);
          setListening(null);
          setNote('');
        }}
        className="text-sm font-semibold text-white bg-[#07070a] hover:bg-violet-500/10 border border-violet-400/30 rounded-xl px-4 py-2"
      >
        Standard-Belegung
      </button>
    </section>
  );
};
